import axios from 'axios';
import posthog from 'posthog-js';
import { boostQuietRecording } from './speech';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Phrases API
export const phrasesApi = {
    getAll: (collectionId = null) => axios.get(`${API}/phrases${collectionId ? `?collection_id=${collectionId}` : ''}`),
    getById: (id) => axios.get(`${API}/phrases/${id}`),
    create: (data) => axios.post(`${API}/phrases`, data),
    createBulk: (phrases) => axios.post(`${API}/phrases/bulk`, { phrases }),
    update: (id, data) => axios.put(`${API}/phrases/${id}`, data),
    delete: (id) => axios.delete(`${API}/phrases/${id}`),
    getAlternatives: (id) => axios.post(`${API}/phrases/${id}/alternatives`),
    getForPractice: (collectionIds = null, mode = 'smart') => {
        return axios.post(`${API}/phrases/practice`, { 
            collection_ids: collectionIds && collectionIds.length > 0 ? collectionIds : null,
            mode: mode
        });
    },
};

// Collections API
export const collectionsApi = {
    getAll: () => axios.get(`${API}/collections`),
    create: (name) => axios.post(`${API}/collections`, { name }),
    update: (id, name) => axios.put(`${API}/collections/${id}`, { name }),
    delete: (id) => axios.delete(`${API}/collections/${id}`),
    addPhrases: (collectionId, phraseIds) => axios.post(`${API}/collections/${collectionId}/phrases`, { phrase_ids: phraseIds }),
    removePhrase: (collectionId, phraseId) => axios.delete(`${API}/collections/${collectionId}/phrases/${phraseId}`),
};

// Export API
export const exportApi = {
    exportCSV: (collectionId = null) => {
        const url = collectionId 
            ? `${API}/export?collection_id=${collectionId}`
            : `${API}/export`;
        return url;
    },
};

// Practice API
export const practiceApi = {
    save: (data) => axios.post(`${API}/practice`, data),
    recordListen: (phraseId, mode = null) => axios.post(`${API}/phrases/${phraseId}/listen`, { mode }),
    recordSessionEnd: (durationSeconds, mode = null) => axios.post(`${API}/session-end`, { duration_seconds: durationSeconds, mode }),
    getHistory: (limit = 50) => axios.get(`${API}/practice/history?limit=${limit}`),
    getStats: () => axios.get(`${API}/practice/stats`),
};

// Dashboard API
export const dashboardApi = {
    get: () => axios.get(`${API}/dashboard`),
};

// Streak API
export const streakApi = {
    get: () => axios.get(`${API}/streak`),
    updateGoal: (dailyGoal) => axios.put(`${API}/streak/goal`, { daily_goal: dailyGoal }),
};

// Audio Analysis API
export const analyzeAudioApi = {
    analyzeAudio: async (targetPhrase, audioBlob, mimeType, recDiag = null) => {
        // Quiet recordings (iOS captures ~50x lower) get an adaptive gain boost
        // before upload; normal-volume recordings pass through untouched. On any
        // boost failure the original blob is sent, so nothing is ever lost.
        const { blob, mimeType: finalMime, boosted, gain, rms } =
            await boostQuietRecording(audioBlob, mimeType);
        if (boosted) {
            try {
                posthog.capture('recording_boosted_client_side', {
                    boost_gain: gain,
                    original_rms: rms,
                });
            } catch { /* analytics best-effort */ }
        }
        recDiag?.mark?.('upload', { boosted: !!boosted, gain, rms, bytes: blob?.size, mime: finalMime });
        const send = (b, m) => {
            const form = new FormData();
            form.append('target_phrase', targetPhrase);
            form.append('audio_file', b, 'recording');
            form.append('mime_type', m);
            // Test accounts only (newRecDiag returns null for everyone else).
            if (recDiag) {
                try { form.append('diag', JSON.stringify(recDiag)); } catch { /* diagnostics only */ }
            }
            return axios.post(`${API}/analyze-audio`, form, { timeout: 30000 });
        };
        // iPhones sometimes produce a recording file nothing can read. The
        // recorder keeps a raw copy for that case; if the server refuses the
        // normal file, the raw copy goes instead, so the learner never sees it.
        const wav = audioBlob?.fallbackWav;
        const unreadable = (res) => /didn't come through/i.test(res?.data?.audio_issue || '');
        let res;
        try {
            res = await send(blob, finalMime);
        } catch (err) {
            // Only a server fault: a refused allowance (402/403/429) must not be retried.
            if (!wav || !(err?.response?.status >= 500)) throw err;
            try { posthog.capture('recording_fallback_used', { reason: 'error', http_status: err?.response?.status }); } catch { /* best-effort */ }
            return send(wav, 'audio/wav');
        }
        if (wav && unreadable(res)) {
            try { posthog.capture('recording_fallback_used', { reason: 'unreadable', mime: finalMime, bytes: blob?.size }); } catch { /* best-effort */ }
            return send(wav, 'audio/wav');
        }
        return res;
    },
};

// Translation API
export const translateApi = {
    translate: (frenchText) => axios.post(`${API}/translate`, { french_text: frenchText }),
    translateToFrench: (englishText) => axios.post(`${API}/translate-to-french`, { english_text: englishText }),
    translatePhrase: (frenchText) => axios.post(`${API}/translate-phrase`, { french_text: frenchText }),
};

// User Settings API
export const settingsApi = {
    get: () => axios.get(`${API}/settings`),
    update: (data) => axios.put(`${API}/settings`, data),
};

// Billing API. Upgrading is a Stripe payment link (see lib/billing.js);
// everything after that — what they are on, ending it, changing their mind —
// happens in the app.
export const billingApi = {
    getSubscription: () => axios.get(`${API}/billing/subscription`),
    cancel: (reason, detail) => axios.post(`${API}/billing/cancel`, { reason, detail }),
    downgrade: () => axios.post(`${API}/billing/downgrade`),
    resume: () => axios.post(`${API}/billing/resume`),
};

// The November 30-day challenge page (pages/Challenge.jsx).
export const challengeApi = {
    status: () => axios.get(`${API}/challenge/status`),
    me: () => axios.get(`${API}/challenge/me`),
    checkout: () => axios.post(`${API}/challenge/checkout`),
};

// Premade collections (Phrases page) and onboarding
export const premadeApi = {
    list: () => axios.get(`${API}/premade-collections`),
    add: (topic_ids) => axios.post(`${API}/premade-collections`, { topic_ids }),
    // A collection's phrases to practise without adding them (sound lessons).
    phrases: (topic_id) => axios.get(`${API}/premade-collections/${topic_id}/phrases`),
};

export const onboardingApi = {
    getTopics: () => axios.get(`${API}/onboarding/topics`),
    getRedo: () => axios.get(`${API}/onboarding/redo`),
    complete: (data) => axios.post(`${API}/onboarding/complete`, data),
};

// Auth API
export const authApi = {
    forgotPassword: (email) => axios.post(`${API}/auth/forgot-password`, { email }),
    resetPassword: (token, new_password) => axios.post(`${API}/auth/reset-password`, { token, new_password }),
};

// Reminders API
export const remindersApi = {
    unsubscribe: (token) => axios.post(`${API}/reminders/unsubscribe`, { token }),
};

// AI Generation API
export const generateApi = {
    generatePhrases: (category = 'common', count = 10, extraContext = '', collectionIds = [], formality = 'informal', gender = null, historyContext = null) =>
        axios.post(`${API}/generate-phrases`, { category, count, extra_context: extraContext || null, collection_ids: collectionIds, formality, gender, history_context: historyContext || null }),
    getHistory: (collectionId = null) => axios.get(`${API}/generate-phrases/history${collectionId ? `?collection_id=${collectionId}` : ''}`),
};


